import { useEffect, type ReactNode } from 'react'

import { paletteFrom } from '../lib/brand-color.ts'
import { useBranding } from '../lib/use-branding.ts'

/**
 * A cor da loja, aplicada (F169).
 *
 * ## Por que escreve no `<html>` e não num `<style>` deste componente
 *
 * Os tokens são lidos por TUDO — o menu lateral, os diálogos que o Radix
 * desenha num portal fora desta árvore, a barra de tarefas do Modo PC. Uma
 * folha local alcançaria só os descendentes, e o diálogo sairia azul-Aeris em
 * cima de uma tela da cor do cliente.
 *
 * ## Sobrescreve a camada semântica, nunca a paleta crua
 *
 * `--brand-primary` e companhia são indireções que `tokens.css` já declarava; a
 * paleta `--aeris-*` fica intacta, e é o que garante que os semáforos de
 * situação (verde "terminou", vermelho "cancelado") continuem sendo do produto.
 * Uma loja não repinta o significado das cores — ver `docs/UI_UX.md`.
 *
 * ## Não há piscada de cor porque não há cor a piscar
 *
 * Enquanto a consulta não volta, valem os tokens do arquivo. A loja sem marca
 * própria nunca chega a perguntar (`useBranding` não dispara), então isto custa
 * exatamente nada para a esmagadora maioria das contas.
 */
export const BrandingProvider = ({
  children,
}: {
  children: ReactNode
}): ReactNode => {
  const { primaryColor } = useBranding()

  useEffect(() => {
    const root = document.documentElement
    const palette = primaryColor === null ? null : paletteFrom(primaryColor)

    const tokens: Record<string, string | null> = {
      '--brand-primary': palette?.primary ?? null,
      '--brand-primary-hover': palette?.primaryHover ?? null,
      '--brand-primary-active': palette?.primaryActive ?? null,
      '--sidebar-bg': palette?.sidebarBg ?? null,
    }
    /*
     * `--brand-primary-subtle` fica de FORA, e é a parte que merece a nota.
     *
     * Um `style` inline no `<html>` vence a folha de estilo, inclusive as
     * regras de `[data-theme='dark']` — então um valor escrito aqui vale nos
     * dois temas. Isso está certo para a cor do botão, que leva texto branco
     * em qualquer tema, e está ERRADO para o fundo de destaque: no tema claro
     * ele é quase branco e no escuro é quase preto. Um valor só apagaria o
     * contraste de um dos dois.
     *
     * O fundo de destaque continua com o tom do Aeris. É uma faixa fina atrás
     * de um selo, e trocá-la corretamente exigiria injetar as duas regras de
     * tema em vez de um `style` — trabalho que esta leva não paga.
     */

    for (const [name, value] of Object.entries(tokens)) {
      if (value === null) root.style.removeProperty(name)
      else root.style.setProperty(name, value)
    }

    return () => {
      for (const name of Object.keys(tokens)) root.style.removeProperty(name)
    }
  }, [primaryColor])

  return children
}
